// src/lib/uploadApi.ts
import axios from "axios";
import { attachHttpLogging } from "../utils/logging/attachHttpLogging";
import { authReady } from "../lib/authBoot";

const BASE_URL = (process.env.NEXT_PUBLIC_BASE_URL as string | undefined) || "/api";
const isBrowser = typeof window !== "undefined";
const isProduction = process.env.NODE_ENV === "production";

const uploadApi = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

uploadApi.interceptors.request.use(
  async (config) => {
    await authReady;
    config.headers = config.headers ?? {};
    if (isBrowser) {
      const accessToken = window.localStorage.getItem("access_token");
      if (accessToken) config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    if (isProduction) config.headers["X-Internal-Request"] = "yes";
    // DO NOT set Content-Type for FormData; browser will do it
    return config;
  },
  (error) => Promise.reject(error)
);

uploadApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: any = error.config;
    if (!error.response) return Promise.reject(error);

    if (error.response.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      if (isBrowser) {
        const refreshToken = window.localStorage.getItem("refresh_token");
        if (refreshToken) {
          try {
            const resp = await axios.post(`${BASE_URL}/auth/refresh/`, { refresh: refreshToken });
            const newAccessToken = resp.data.access;
            window.localStorage.setItem("access_token", newAccessToken);

            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            if (isProduction) originalRequest.headers["X-Internal-Request"] = "yes";
            return uploadApi(originalRequest);
          } catch (refreshError) {
            window.localStorage.removeItem("access_token");
            window.localStorage.removeItem("refresh_token");
            if (typeof window !== "undefined") window.location.href = "/";
            return Promise.reject(refreshError);
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Attach unified HTTP error logging AFTER refresh flow
attachHttpLogging(uploadApi, "uploadApi");

export default uploadApi;
